import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Parst einen deutschen Zahlenwert aus einem String.
 * Erkennt Tausenderpunkt (15.000) und Kommazahl (1,2).
 * Gibt ein Objekt mit dem numerischen Wert und dem Format zurück.
 */
function parseCounterValue(rawText) {
    const text = rawText.trim();

    // Kommazahl erkennen: "1,2", "5,7" etc.
    const commaMatch = text.match(/^(\d+),(\d+)$/);
    if (commaMatch) {
        const decimalPlaces = commaMatch[2].length;
        return {
            value: parseFloat(text.replace(',', '.')),
            format: 'decimal',
            decimalPlaces,
        };
    }

    // Tausenderpunkt erkennen: "15.000", "30.000"
    const thousandMatch = text.match(/^[\d.]+$/) && text.includes('.');
    if (thousandMatch) {
        return {
            value: parseInt(text.replace(/\./g, ''), 10),
            format: 'thousand',
            decimalPlaces: 0,
        };
    }

    // Ganzzahl: "180"
    return {
        value: parseInt(text, 10),
        format: 'integer',
        decimalPlaces: 0,
    };
}

/**
 * Formatiert einen numerischen Wert zurück in das deutsche Format.
 * Verwendet keine toLocaleString-API (zu langsam in Firefox) sondern
 * eine manuelle Regex-Lösung für bessere Frame-Performance.
 */
function formatCounterValue(numericValue, format, decimalPlaces) {
    if (format === 'decimal') {
        return numericValue.toFixed(decimalPlaces).replace('.', ',');
    }

    if (format === 'thousand') {
        return Math.round(numericValue)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    return Math.round(numericValue).toString();
}

/**
 * Initialisiert die Counter-Animation für alle [data-counter="true"] Elemente.
 * Startet die Animation per ScrollTrigger, sobald das Element im Viewport sichtbar wird.
 */
function initCounters() {
    const counterElements = document.querySelectorAll('[data-counter="true"]');

    if (!counterElements.length) return;

    counterElements.forEach((element) => {
        const rawText = element.textContent;
        const parsed = parseCounterValue(rawText);

        if (isNaN(parsed.value)) return;

        // Span vor Screen Readern verstecken – eine sr-only-Kopie
        // des Endwerts wird daneben eingefügt, damit AT den korrekten
        // Wert sofort lesen können, ohne die Animation zu verfolgen.
        element.setAttribute('aria-hidden', 'true');
        const srSpan = document.createElement('span');
        srSpan.className = 'sr-only';
        srSpan.textContent = rawText.trim();
        element.parentNode.insertBefore(srSpan, element);

        // Startwert auf 0 setzen
        element.textContent = formatCounterValue(0, parsed.format, parsed.decimalPlaces);

        const animationObject = { val: 0 };

        // Bei prefers-reduced-motion direkt den Endwert setzen (Accessibility + Firefox-Fix)
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
            element.textContent = formatCounterValue(parsed.value, parsed.format, parsed.decimalPlaces);
            return;
        }

        ScrollTrigger.create({
            trigger: element,
            start: 'top 85%',
            once: true,
            onEnter: () => {
                gsap.to(animationObject, {
                    val: parsed.value,
                    duration: 2.5,
                    ease: 'power1.out',
                    onUpdate: () => {
                        element.textContent = formatCounterValue(
                            animationObject.val,
                            parsed.format,
                            parsed.decimalPlaces
                        );
                    },
                });
            },
        });
    });
}

initCounters();
